"use client";
import React, { useState } from "react";
import styles from "./ArchitectedBlueprint.module.css";
import { aiChatBotBlueprintNodes as NODES } from "../../data/ai-chat-bot";

export default function ArchitectedBlueprint() {
  const [active, setActive] = useState(NODES[0].id);

  const onSelect = (id) => {
    setActive(id === active ? null : id);
  };

  const activeNode = NODES.find((n) => n.id === active);
  // keyboard navigation order
  const order = NODES.map((n) => n.id);
  function handleKeyNav(e) {
    if (!["ArrowLeft", "ArrowUp", "ArrowRight", "ArrowDown"].includes(e.key))
      return;
    e.preventDefault();
    const idx = order.indexOf(active) === -1 ? 0 : order.indexOf(active);
    const len = order.length;
    let ni = idx;
    if (e.key === "ArrowLeft" || e.key === "ArrowUp")
      ni = (idx - 1 + len) % len;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") ni = (idx + 1) % len;
    setActive(order[ni]);
  }

  return (
    <section className={styles.section} aria-labelledby="blueprint-title">
      <div className="container">
        <div className={styles.headerWrap}>
          <div className="text-center mb-4" data-reveal="fade">
            <p
              className="text-uppercase small"
              style={{ letterSpacing: ".12em", color: "#F48428" }}
            >
              Architecture
            </p>
            <h2 id="blueprint-title" className="fw-bold text-white">
              Architected for Intelligence & Scale
            </h2>
            <p className="">
              Explore the anatomy of our AI. Discover the interconnected pillars
              that power every conversation.
            </p>
          </div>
        </div>

        <div className="row align-items-start g-4">
          <div className="col-12 col-lg-7">
            <div
              className={styles.canvas}
              role="application"
              aria-label="AI blueprint"
              tabIndex={0}
              onKeyDown={handleKeyNav}
            >
              <div className={styles.centerCore} aria-hidden>
                <div className={styles.coreInner}>AI Core</div>
              </div>

              {NODES.map((node, i) => (
                <button
                  key={node.id}
                  className={`${styles.node} ${active === node.id ? styles.nodeActive : ""
                    } ${styles[`pos${i + 1}`]}`}
                  onClick={() => onSelect(node.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onSelect(node.id);
                  }}
                  aria-pressed={active === node.id}
                >
                  <span className={styles.nodeLabel}>{node.label}</span>
                </button>
              ))}

              {/* connecting lines (decorative) */}
              <svg className={styles.lines} aria-hidden>
                <defs>
                  <linearGradient id="lg1" x1="0%" x2="100%">
                    <stop offset="0%" stopColor="#ff9a3c" stopOpacity="0.7" />
                    <stop offset="100%" stopColor="#ff5b00" stopOpacity="0.7" />
                  </linearGradient>
                </defs>
                <line
                  id="l-conversational"
                  className={
                    active === "conversational" ? styles.lineActive : ""
                  }
                  x1="50%"
                  y1="50%"
                  x2="20%"
                  y2="20%"
                  stroke="url(#lg1)"
                  strokeWidth="1"
                  strokeOpacity="0.22"
                />
                <line
                  id="l-integration"
                  className={active === "integration" ? styles.lineActive : ""}
                  x1="50%"
                  y1="50%"
                  x2="80%"
                  y2="20%"
                  stroke="url(#lg1)"
                  strokeWidth="1"
                  strokeOpacity="0.22"
                />
                <line
                  id="l-learning"
                  className={active === "learning" ? styles.lineActive : ""}
                  x1="50%"
                  y1="50%"
                  x2="20%"
                  y2="80%"
                  stroke="url(#lg1)"
                  strokeWidth="1"
                  strokeOpacity="0.22"
                />
                <line
                  id="l-security"
                  className={active === "security" ? styles.lineActive : ""}
                  x1="50%"
                  y1="50%"
                  x2="80%"
                  y2="80%"
                  stroke="url(#lg1)"
                  strokeWidth="1"
                  strokeOpacity="0.22"
                />
              </svg>
            </div>
          </div>

          <div className="col-12 col-lg-5">
            <div
              className={`${styles.info} ${activeNode ? styles.infoEnter : ""}`}
              aria-live="polite"
            >
              {activeNode ? (
                <div>
                  <h3 className={styles.infoTitle}>{activeNode.title}</h3>
                  <p className={styles.infoDesc}>{activeNode.desc}</p>
                  <h4 className={styles.infoTechTitle}>Core Technologies</h4>
                  <ul className={styles.infoTechList}>
                    {activeNode.tech.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className={styles.placeholder}>
                  Click a component to explore.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
